import { useEffect, useRef, useState } from "react";
import {
  getRoomPreview,
  getTelegramConfig,
  type RoomPreview,
  type TelegramConfig,
} from "../lib/api.js";
import {
  getNickname,
  setNickname,
  getAvatar,
  setAvatar,
  getHostToken,
} from "../lib/storage.js";
import { TelegramLogin } from "./TelegramLogin.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
function savedAdmission(code: string): string | null {
  try {
    return sessionStorage.getItem("kinop.admission." + code);
  } catch {
    return null;
  }
}
export function JoinGate({
  code,
  onEnter,
  onNavigate,
}: {
  code: string;
  onEnter: (name: string, token: string | null) => void;
  onNavigate: (p: string) => void;
}): JSX.Element {
  const [name, setName] = useState(getNickname);
  const [room, setRoom] = useState<(RoomPreview & { access?: string }) | null>(
    null,
  );
  const [error, setError] = useState("");
  const [config, setConfig] = useState<TelegramConfig | null>(null);
  const [token, setToken] = useState<string | null>(() => savedAdmission(code));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const enteringRef = useRef(false);
  useEffect(() => {
    try {
      if (token) sessionStorage.setItem("kinop.admission." + code, token);
      else sessionStorage.removeItem("kinop.admission." + code);
    } catch {}
  }, [code, token]);
  useEffect(() => {
    let done = false;
    setLoading(true);
    setRoom(null);
    setError("");
    void getRoomPreview(code)
      .then((r) => {
        if (!done) setRoom(r);
      })
      .catch(() => {
        if (!done) setError("Комната не найдена или уже закрыта.");
      })
      .finally(() => {
        if (!done) setLoading(false);
      });
    void getTelegramConfig()
      .then((c) => {
        if (!done) setConfig(c);
      })
      .catch(() => {});
    return () => {
      done = true;
    };
  }, [code]);
  useEffect(() => {
    if (!token) return;
    let done = false;
    let inFlight = false;
    const poll = async () => {
      if (inFlight || done) return;
      inFlight = true;
      try {
        const r = await fetch(`/api/rooms/${code}/admission`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.status === 401 || r.status === 404 || r.status === 403) throw new Error("Запрос истёк. Постучитесь ещё раз.");
        if (!r.ok) throw new Error("Нет связи с сервером. Повторяем проверку…");
        const data = await r.json();
        if (done) return;
        setError("");
        if (data.status === "approved" && name.trim().length >= 2) {
          done = true;
          setNickname(name.trim());
          onEnter(name.trim(), token);
        }
        if (data.status === "denied") {
          setError("Ведущий отклонил запрос.");
          setToken(null);
        }
      } catch (e) {
        if (!done) {
          setError(e instanceof Error ? e.message : "Нет связи");
          // A transient outage must not discard an approved admission token.
          if (e instanceof Error && e.message.startsWith("Запрос истёк")) setToken(null);
        }
      } finally {
        inFlight = false;
      }
    };
    const timer = setInterval(() => void poll(), 2000);
    void poll();
    return () => {
      done = true;
      clearInterval(timer);
    };
  }, [token, code, name, onEnter]);
  async function enter(candidate = name) {
    if (enteringRef.current || token || !room || room.isClosed) return;
    setError("");
    const nickname = candidate.trim().slice(0, 24);
    if (nickname.length < 2) {
      setError("Хотя бы две буквы — чтобы вас различали.");
      return;
    }
    enteringRef.current = true;
    setSubmitting(true);
    setName(nickname);
    setNickname(nickname);
    try {
      if (room.access === "approval" && !getHostToken(code)) {
        const r = await fetch(`/api/rooms/${encodeURIComponent(code)}/knock`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ nickname }),
          signal: AbortSignal.timeout(20000),
        });
        if (!r.ok) throw new Error("Не удалось отправить запрос. Попробуйте позже.");
        setToken((await r.json()).token);
        return;
      }
      onEnter(nickname, null);
    } catch (e) {
      setError(e instanceof Error && e.name === "TimeoutError"
        ? "Сервер не ответил. Попробуйте ещё раз."
        : e instanceof Error ? e.message : "Нет связи с сервером.");
    } finally {
      enteringRef.current = false;
      setSubmitting(false);
    }
  }
  return (
    <main className="gate">
      <div className="card gate__card">
        <button className="chip" onClick={() => onNavigate("/")}>
          ← На главную
        </button>
        <div className="guest-preview__frame" style={{ marginTop: 20 }}>
          ▷
        </div>
        <h1 className="card__title">
          {loading
            ? "Ищем комнату…"
            : (room?.name ?? "Этой комнаты больше нет")}
        </h1>
        {room && (
          <>
            <p className="helper">
              {room.sourceTitle ?? "Видео ещё не выбрано"}
              <br />
              {room.participantCount} в комнате ·{" "}
              {room.isClosed ? "сеанс закончился" : "ждут вас"}
            </p>
            {!room.isClosed && (
              <>
                {config?.enabled && config.botUsername && (
                  <TelegramLogin
                    botUsername={config.botUsername}
                    onLogin={async (u) => {
                      setName(u.displayName);
                      if (u.avatarUrl)
                        setAvatar({ ...getAvatar(), photoUrl: u.avatarUrl });
                      await enter(u.displayName);
                    }}
                  />
                )}
                <p className="helper">или просто именем</p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    void enter();
                  }}
                >
                  <label htmlFor="gate-nickname">Как вас представить?</label>
                  <Input
                    id="gate-nickname"
                    value={name}
                    maxLength={24}
                    disabled={!!token || submitting}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Например, Саша"
                    autoComplete="nickname"
                  />
                  <p className="helper">Можно прозвище. Меняется в профиле.</p>
                  <Button
                    type="submit"
                    className="w-full"
                    size="lg"
                    disabled={!!token || submitting}
                  >
                    {submitting
                      ? "Входим…"
                      : token
                      ? "Ждём одобрения ведущего…"
                      : room.access === "approval" && !getHostToken(code)
                        ? "Постучаться"
                        : "Войти в комнату"}
                  </Button>
                  {token && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setToken(null)}
                    >
                      Отменить ожидание
                    </Button>
                  )}
                </form>
              </>
            )}
          </>
        )}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {((!room && !loading) || room?.isClosed) && (
          <Button onClick={() => onNavigate("/new")}>
            Создать свою комнату
          </Button>
        )}
      </div>
    </main>
  );
}
export function AdmissionRequests({
  code,
}: {
  code: string;
}): JSX.Element | null {
  const [requests, setRequests] = useState<
    { token: string; nickname: string }[]
  >([]);
  const [error, setError] = useState("");
  useEffect(() => {
    let done = false;
    async function load() {
      try {
        const r = await fetch(`/api/rooms/${code}/requests`, {
          headers: { Authorization: `Bearer ${getHostToken(code)}` },
        });
        if (r.ok && !done) setRequests((await r.json()).requests);
      } catch {}
    }
    void load();
    const timer = setInterval(() => void load(), 3000);
    return () => {
      done = true;
      clearInterval(timer);
    };
  }, [code]);
  async function decide(token: string, accept: boolean) {
    try {
      const r = await fetch(`/api/rooms/${code}/requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getHostToken(code)}`,
        },
        body: JSON.stringify({ token, accept }),
      });
      if (!r.ok) throw new Error();
      setRequests((v) => v.filter((r) => r.token !== token));
      setError("");
    } catch {
      setError("Не удалось ответить. Попробуйте ещё раз.");
    }
  }
  return requests.length ? (
    <div className="card" style={{ margin: 12, padding: 12 }}>
      <p>Просятся в комнату</p>
      {requests.map((r) => (
        <div key={r.token}>
          <p>{r.nickname}</p>
          <Button size="sm" onClick={() => void decide(r.token, true)}>
            Впустить
          </Button>{" "}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void decide(r.token, false)}
          >
            Отказать
          </Button>
        </div>
      ))}
      {error && <p className="error">{error}</p>}
    </div>
  ) : null;
}
